// An invoice as a collection of jobs (finance-real, the user 2026-10-22), each drawer named in the URL (DD-045, DD-051).
// It knows no quote: jobs are in between. ?modal=job-invoice&invoice=new: New invoice, picking from the client's done and
// active jobs, any period mixed, the done ones ticked; the draft it makes turns them invoiced, which is how a job is
// finished, even an active one: picking it says it was delivered. ?modal=job-invoice&invoice=<id>: its jobs, each opening its drawer in
// place of this one, and its figures (the server's, TD-102): what the jobs bill, what it passed along on no job (ads,
// costs; on the client's newest draft, each item its Send would pass along today), what it asks and what is still due. A draft takes more done or active jobs, and a job
// taken off it is done again; the draft goes with its last job. Its status is the platform's status dropdown (DD-062),
// here and on its row: Draft and Sent are picked (Sent stamps the day and fixes the number the draft was prefilled
// with), Partly paid is a payment's, and Paid opens a footer step recording a payment, what is still due filled in
// (`confirm=paid` from a row), with Paid in full for a short one (answer e); the status follows the payment. Overdue is
// the server's, from its date, and is never picked. A draft's number is the server's next, edited in place until Sent
// fixes it (answer g), and Sent waits while an edit of it is saving or refused. A payment and a status change send a
// request key (useRequestKeys), so a retry never writes twice.
// Laid out as the month drawer (the user 2026-10-23: "this should also be used in ... the invoice modal view"), its body
// is The invoice (as it stands, on a draft): its jobs, what it passed along, the Total; a draft's Add jobs to bill
// and New job, which stacks the job form over it (`stack=line-job-form`, DD-045) and makes the job active on it. Then
// Goes out for it: every claim on its jobs (ClaimsList, the job drawer's), pressed open to change or remove, Add a claim
// on a job with room, the Claimed total; and Margin, what its jobs bill less their claims. Still due sits by its
// status. Its jobs, New invoice's and Add jobs to bill's picks are the shared JobLine.
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { DayField } from "@/components/ui/DayField";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { JobLine } from "../components/JobLine";
import { Required } from "../components/Required";
import { useFieldAutosave } from "../hooks/useFieldAutosave";
import { useRequestKeys, type RequestKeys } from "../hooks/useRequestKeys";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { JobInvoice, JobInvoiceStatus, LineJob } from "../types";
import { ClaimsList } from "./ClaimsList";
import { prefixed } from "../utils/format";
import { awaitsPayment, invoiceDateLine, jobSubline } from "../utils/names";
import { closeUnlessPending } from "../utils/mutations";
import { useJobInvoiceDrawer, type JobInvoiceDrawerModel } from "./hooks/useJobInvoiceDrawer";
import { useNewJobInvoiceDrawer } from "./hooks/useNewJobInvoiceDrawer";
import { LineJobFormDrawer } from "./LineJobForms";
import { JOB_INVOICE_STATES, JOB_INVOICE_STATUSES, JOB_STATUS, jobInvoiceTitle, listPriceText } from "./lineJobText";
import { StatusBadge, StatusPill } from "./StatusBadge";

// `contract` names the user's job now (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobsWord = JOB.plural.toLowerCase();
const INVOICE = ENTITY_CONFIG.invoice;
const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";

/** A job an invoice takes, as the server has it: done, or active (picking it says it was delivered). */
export const toBill = (job: LineJob): boolean => job.status === "done" || job.status === "active";

/** Jobs to tick, each the shared JobLine: name, period and how it bills, its price, its status pill. New invoice's and a
 * draft's Add jobs to bill, and the quote drawer's and form's draft jobs. */
export function JobPicks({ jobs, picked, onToggle, disabled = false }: { jobs: LineJob[]; picked: string[]; onToggle: (id: string) => void; disabled?: boolean }) {
  return (
    <ul className="divide-y divide-gray-100">
      {jobs.map((j) => (
        <li key={j.id}>
          <JobLine
            name={j.name}
            sub={jobSubline(j)}
            note={listPriceText(j)}
            amountMinor={j.priceMinor}
            check={{ checked: picked.includes(j.id), onChange: () => onToggle(j.id), disabled }}
            trailing={<StatusPill look={JOB_STATUS[j.status]} />}
          />
        </li>
      ))}
    </ul>
  );
}

export const toggled = (ids: string[], id: string): string[] => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

export function NewJobInvoiceDrawer({ clientId, titlePrefix, billable, picks, stacked = false, onCreated, onClose }: {
  clientId: string;
  /** Whose invoice it is, on /finance, in front as every invoice drawer's title has it ("Mister Coffee · New invoice"). */
  titlePrefix?: string;
  /** The client's done and active jobs (toBill). */
  billable: LineJob[];
  /** The jobs to tick first, To do's done jobs as the server named them (`pick=`); without them, the done ones here. */
  picks?: string[];
  /** Over the client drawer on /finance (`stack=new-invoice`): no second backdrop (DD-045). */
  stacked?: boolean;
  onCreated: (invoiceId: string) => void;
  onClose: () => void;
}) {
  const { create } = useNewJobInvoiceDrawer(clientId);
  const send = useSubmitOnce();
  const [picked, setPicked] = useState<string[]>(() => billable.filter((j) => (picks ? picks.includes(j.id) : j.status === "done")).map((j) => j.id));
  return (
    <FinanceDrawer
      title={prefixed(titlePrefix, INVOICE.createLabel)}
      onClose={closeUnlessPending(create.isPending, onClose)}
      stacked={stacked}
      footer={
        <Button disabled={picked.length === 0 || create.isPending} isLoading={create.isPending} onClick={() => send(() => create.mutateAsync({ jobIds: picked }), (i) => onCreated(i.id))}>
          Make the draft
        </Button>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs text-gray-500">{`Pick the ${jobsWord} it bills, done or under way, any period mixed. On the draft they read invoiced; one taken off is done.`}</p>
        {billable.length === 0 ? <p className="text-gray-400">{`No ${JOB.label.toLowerCase()} done or under way to bill.`}</p> : <JobPicks jobs={billable} picked={picked} onToggle={(id) => setPicked((p) => toggled(p, id))} />}
      </div>
    </FinanceDrawer>
  );
}

interface InvoiceStatusProps {
  invoice: JobInvoice;
  onPick: (status: JobInvoiceStatus) => void;
  loading?: boolean;
  align?: "left" | "right";
  /** Why Sent waits now: the draft's number is saving, or the server refused it. */
  sentHeld?: string;
}

/** What sets a status the user never picks: a payment, or the invoice's date. */
const SET_BY: Partial<Record<JobInvoiceStatus, string>> = { partial: "By a payment", overdue: "By its date" };

/** An invoice's status dropdown, on its row and in its drawer (DD-062). Every invoice's menu lists the same five, Draft,
 * Sent, Overdue, Partly paid and Paid: Draft and Sent until money arrives; Paid records a payment once sent; Overdue (its
 * date's) and Partly paid (a payment's) are never picked, so they are always disabled with what sets them. Every other
 * closed choice says why. A paid invoice holds. Sent waits for the draft's number (`sentHeld`). */
export function InvoiceStatusBadge({ invoice, onPick, loading = false, align = "left", sentHeld }: InvoiceStatusProps) {
  const money = invoice.receivedMinor > 0 || invoice.status === "partial" || invoice.status === "paid";
  const why = (s: JobInvoiceStatus): string | undefined => {
    if (s === "paid") return invoice.status === "draft" ? "Once sent" : undefined;
    if (money) return "A payment arrived";
    // An overdue invoice is a sent one past its date: Sent again changes nothing.
    if (s === "sent" && invoice.status === "overdue") return "Already sent";
    if (s === "sent" && invoice.status === "draft") return sentHeld;
    return undefined;
  };
  const open = (s: JobInvoiceStatus): boolean => SET_BY[s] === undefined && (s === "paid" ? awaitsPayment(invoice.status) : why(s) === undefined);
  return (
    <StatusBadge<JobInvoiceStatus>
      value={invoice.status}
      choices={JOB_INVOICE_STATUSES.map((s) => ({ value: s, look: JOB_INVOICE_STATES[s], setBy: SET_BY[s], disabled: !open(s), why: why(s) }))}
      onPick={onPick}
      loading={loading}
      align={align}
      ariaLabel={`${jobInvoiceTitle(invoice)} status`}
    />
  );
}

/** The footer step Paid opens: what arrived and when, what is still due filled in. The status follows the payment: less
 * than due leaves it Partly paid, unless Paid in full is ticked (answer e): from the server's `leastInFullMinor` up, the
 * rest was withheld and it closes Paid; below it, the rest is written off, which asks why. Without that figure it asks
 * why too, so it never closes Paid on a guess. Paid in full has to cover the costs passed along first, which only the
 * server knows: the copy promises no write-off, and its refusal shows here. The screen compares the amount with the
 * server's figures and works out none of its own (TD-102). Each attempt sends one request key, kept on its retry. */
function PaymentStep({ invoice, today, pay, keys, onDone }: { invoice: JobInvoice; today: string; pay: JobInvoiceDrawerModel["pay"]; keys: RequestKeys; onDone: () => void }) {
  const [amountMinor, setAmountMinor] = useState<number | null>(invoice.dueMinor);
  const [receivedOn, setReceivedOn] = useState(today);
  const [inFull, setInFull] = useState(false);
  const [reason, setReason] = useState("");
  const short = amountMinor !== null && amountMinor > 0 && amountMinor < invoice.dueMinor;
  // Paid in full counts only while the amount is short of what is due.
  const paidInFull = inFull && short;
  // Fail safe: with no least from the server, Paid in full asks the write-off's reason.
  const writesOff = paidInFull && amountMinor !== null && (!Number.isFinite(invoice.leastInFullMinor) || amountMinor < invoice.leastInFullMinor);
  const withheld = Number.isFinite(invoice.withholdingPct) ? `${invoice.withholdingPct}% of the work` : "what may be withheld of the work";
  const passed = invoice.passedMinor > 0 ? " The costs passed along have to be covered first." : "";
  const valid = amountMinor !== null && amountMinor > 0 && amountMinor <= invoice.dueMinor && receivedOn !== "" && (!writesOff || reason.trim() !== "");
  const scope = `${invoice.id}:payment`;
  const record = () => {
    if (amountMinor === null || !valid) return;
    pay.mutate(
      { invoiceId: invoice.id, input: { amountMinor, receivedOn, paidInFull, reason: writesOff ? reason.trim() : null, requestId: keys.keyFor(scope) } },
      {
        onSuccess: () => {
          keys.settled(scope);
          onDone();
        },
        onError: (error) => keys.settled(scope, error),
      },
    );
  };
  return (
    <div className="w-full space-y-3">
      <p className="text-sm text-gray-700">
        {"Record a payment: "}
        <MoneyText amountMinor={invoice.dueMinor} strong />
        {" is still due. Less than that leaves it partly paid, unless it was paid in full."}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Amount">
          <MoneyInput ariaLabel="Amount" currency="EUR" valueMinor={amountMinor} onChange={setAmountMinor} invalid={amountMinor !== null && amountMinor > invoice.dueMinor} autoFocus />
        </FormField>
        <FormField label="Arrived on">
          <DayField ariaLabel="Arrived on" value={receivedOn} onChange={setReceivedOn} />
        </FormField>
      </div>
      {short && (
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={inFull} disabled={pay.isPending} onChange={(e) => setInFull(e.target.checked)} />
          <span>
            Paid in full: nothing more will come
            <span className="block text-xs text-gray-500">
              {!paidInFull
                ? `With up to ${withheld} withheld it closes paid; a bigger gap asks why the rest is left unpaid.${passed}`
                : writesOff
                  ? `More than ${withheld} is left unpaid: give the reason to write off the rest.${passed}`
                  : `The rest was withheld: it closes paid.${passed}`}
            </span>
          </span>
        </label>
      )}
      {writesOff && (
        <FormField label={<Required>Why it is written off</Required>}>
          <input className={inputClassName} aria-label="Why it is written off" aria-required="true" value={reason} maxLength={200} placeholder="e.g. The business closed" onChange={(e) => setReason(e.target.value)} />
        </FormField>
      )}
      {pay.error && <p className="text-xs text-red-600">{pay.error.message}</p>}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" disabled={pay.isPending} onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={!valid || pay.isPending} isLoading={pay.isPending} onClick={record}>
          {amountMinor !== null && amountMinor > 0 ? `Record payment · ${formatMoney(amountMinor, "EUR")}` : "Record payment"}
        </Button>
      </div>
    </div>
  );
}

type NumberAutosave = ReturnType<typeof useFieldAutosave<"number">>;

/** Why Sent waits on a draft's number: an edit saving, or one the server refused. Undefined once it is saved. */
export const sentHeldBy = (autosave: Pick<NumberAutosave, "state" | "errors">): string | undefined =>
  autosave.state === "saving" ? "Saving the number" : autosave.errors.number !== undefined ? "Fix the number first" : undefined;

/** A draft's number, prefilled by the server with the next after the agency's highest (answer g), saved as it is typed;
 * Sent fixes it. A number another invoice holds is refused under the field, keeping what was typed. The autosave is the
 * drawer's, so Sent waits on it. */
function DraftNumber({ invoice, set, autosave }: { invoice: JobInvoice; set: JobInvoiceDrawerModel["setNumber"]; autosave: NumberAutosave }) {
  const [value, setValue] = useState(invoice.number ?? "");
  return (
    <FormField
      label="Number"
      hint={autosave.errors.number ? undefined : autosave.state === "saving" ? "Saving…" : autosave.state === "saved" ? "Saved. Sent fixes it." : "The next in the run. Sent fixes it."}
      error={autosave.errors.number}
    >
      <input
        aria-label="Number"
        className={inputClassName}
        value={value}
        maxLength={40}
        onChange={(e) => {
          setValue(e.target.value);
          const number = e.target.value.trim();
          if (number === "") autosave.hold("number", "A number is required");
          else autosave.schedule("number", () => set.mutateAsync({ invoiceId: invoice.id, input: { number } }));
        }}
      />
    </FormField>
  );
}

interface Props {
  invoice: JobInvoice;
  /** Its client: its claims, and a new job's. */
  clientId: string;
  /** The jobs on it, in its order. */
  jobs: LineJob[];
  /** The client's done and active jobs (toBill), for a draft to take more. */
  billable: LineJob[];
  today: string;
  /** Opened from a row's status dropdown picking Paid: the payment step at once. */
  paying: boolean;
  /** The job form stacked over it (`stack=line-job-form`): a draft's New job, active and on it. */
  stacked: boolean;
  onStack: () => void;
  onUnstack: () => void;
  onOpenJob: (jobId: string) => void;
  onClose: () => void;
  /** Whose invoice it is, on /finance's Invoices, as main's agency drawers name their client. */
  titlePrefix?: string;
}

const totalRow = "flex items-center justify-between gap-3 pt-2 mt-1 border-t border-gray-200";

export function JobInvoiceDrawer({ invoice, clientId, jobs, billable, today, paying: payingFirst, stacked, onStack, onUnstack, onOpenJob, onClose, titlePrefix }: Props) {
  const { add, remove, setStatus, setNumber, pay, claims, people, addClaim, updateClaim, removeClaim } = useJobInvoiceDrawer(clientId);
  const numberSave = useFieldAutosave<"number">();
  const keys = useRequestKeys();
  const [picked, setPicked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(payingFirst && awaitsPayment(invoice.status));
  const draft = invoice.status === "draft";
  const busy = add.isPending || remove.isPending || setStatus.isPending || pay.isPending;
  const onIt = (claims.data ?? []).filter((c) => invoice.jobIds.includes(c.jobId));
  const pick = (status: JobInvoiceStatus) => {
    if (status === "paid") return setPaying(true);
    setPaying(false);
    if (status !== "draft" && status !== "sent") return;
    const scope = `${invoice.id}:${status}`;
    setStatus.mutate({ invoiceId: invoice.id, input: { status, requestId: keys.keyFor(scope) } }, { onSuccess: () => keys.settled(scope), onError: (error) => keys.settled(scope, error) });
  };
  return (
    <>
      <FinanceDrawer
        onClose={closeUnlessPending(busy, onClose)}
        title={prefixed(titlePrefix, jobInvoiceTitle(invoice))}
        footer={paying ? <PaymentStep invoice={invoice} today={today} pay={pay} keys={keys} onDone={() => setPaying(false)} /> : undefined}
      >
        <div className="space-y-4 text-sm">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <InvoiceStatusBadge invoice={invoice} onPick={pick} loading={setStatus.isPending} sentHeld={draft ? sentHeldBy(numberSave) : undefined} />
              <span className="text-xs text-gray-500">{invoiceDateLine({ kind: "client", ...invoice })}</span>
              {!draft && (
                <span className="text-xs text-gray-500">
                  {"Still due "}
                  <MoneyText amountMinor={invoice.dueMinor} strong />
                </span>
              )}
            </div>
            {draft && <p className="text-xs text-gray-500">{`Its ${jobsWord} read invoiced, and one taken off is done. Sent fixes its number and gives it today's date.`}</p>}
            {invoice.status === "overdue" && <p className="text-xs text-red-700">More than 30 days since it went out, and money is still due. A payment that clears it makes it Paid.</p>}
            {setStatus.error && <p className="text-xs text-red-600">{setStatus.error.message}</p>}
          </div>

          {draft && <DraftNumber invoice={invoice} set={setNumber} autosave={numberSave} />}

          {/* The month drawer's layout (the user 2026-10-23): the invoice, what goes out for it, and the margin below. */}
          <section className="space-y-2">
            <div>
              <h3 className={heading}>{draft ? "The invoice, as it stands" : "The invoice"}</h3>
              <ul className="divide-y divide-gray-100">
                {jobs.map((j) => (
                  <li key={j.id}>
                    {/* DD-045's one exception: a draft's own rows carry Take off, as a form's rows do. */}
                    <JobLine
                      name={j.name}
                      sub={jobSubline(j)}
                      note={listPriceText(j)}
                      amountMinor={j.priceMinor}
                      onOpen={() => onOpenJob(j.id)}
                      trailing={
                        <>
                          <StatusPill look={JOB_STATUS[j.status]} />
                          {draft && (
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate({ invoiceId: invoice.id, jobId: j.id }, { onSuccess: (r) => (r.invoice ? undefined : onClose()) })}>
                              Take off
                            </Button>
                          )}
                        </>
                      }
                    />
                  </li>
                ))}
                {/* A draft lists what its Send would pass along today, each on its own row; sent, its rebill is one row. */}
                {invoice.passing.length > 0
                  ? invoice.passing.map((item) => (
                      <li key={item.key} className="flex items-start justify-between gap-3 py-1.5">
                        <span className="min-w-0 [overflow-wrap:anywhere]">
                          <span className="text-gray-500">{item.label}</span>
                          <span className="block text-xs text-gray-400">Passed along when it is sent</span>
                        </span>
                        <MoneyText amountMinor={item.amountMinor} className="text-sm shrink-0" />
                      </li>
                    ))
                  : invoice.passedMinor !== 0 && (
                      <li className="flex items-start justify-between gap-3 py-1.5">
                        <span className="min-w-0">
                          <span className="text-gray-500">Passed along</span>
                          <span className="block text-xs text-gray-400">Ads and costs, on no job</span>
                        </span>
                        <MoneyText amountMinor={invoice.passedMinor} className="text-sm shrink-0" />
                      </li>
                    )}
              </ul>
              <div className={totalRow}>
                {/* DD-054: the invoice's total is Total, the tables' column too. */}
                <span className="text-sm font-medium text-gray-900">Total</span>
                <MoneyText amountMinor={invoice.invoiceMinor} strong className="text-sm" />
              </div>
            </div>
            {/* Only a draft takes jobs: the client's done or active ones, or a new one stacked over this drawer (DD-045). */}
            {draft && !adding && (
              <div className="flex flex-wrap gap-2">
                {billable.length > 0 && (
                  <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                    {`Add ${jobsWord} to bill`}
                  </Button>
                )}
                <EntityCreateButton entity="contract" variant="secondary" onClick={onStack} />
              </div>
            )}
            {draft && adding && (
              <div className="space-y-2 rounded-lg bg-gray-50 p-3">
                <JobPicks jobs={billable} picked={picked} onToggle={(id) => setPicked((p) => toggled(p, id))} />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setAdding(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={picked.length === 0 || busy}
                    isLoading={add.isPending}
                    onClick={() =>
                      add.mutate(
                        { invoiceId: invoice.id, input: { jobIds: picked } },
                        {
                          onSuccess: () => {
                            setPicked([]);
                            setAdding(false);
                          },
                        },
                      )
                    }
                  >
                    Add to the draft
                  </Button>
                </div>
              </div>
            )}
          </section>

          <section>
            <h3 className={heading}>Goes out for it</h3>
            <ClaimsList
              claims={onIt}
              loading={claims.isLoading}
              jobs={jobs}
              showJob
              people={people}
              add={addClaim}
              update={updateClaim}
              remove={removeClaim}
              total={{ label: "Claimed", amountMinor: invoice.claimedMinor }}
            />
          </section>

          <div className="flex items-center justify-between gap-3 pt-3 border-t-2 border-gray-200">
            <span className="text-sm font-semibold text-gray-900">Margin</span>
            <MoneyText amountMinor={invoice.marginMinor} strong balance className="text-base" />
          </div>
        </div>
      </FinanceDrawer>
      {stacked && draft && <LineJobFormDrawer clientId={clientId} forInvoice={{ invoiceId: invoice.id, title: prefixed(titlePrefix, jobInvoiceTitle(invoice)) }} own={false} today={today} onSaved={onUnstack} onClose={onUnstack} />}
    </>
  );
}
